import { createRandomFileKey } from "@/app-utils/export.tsx";
import type { FileDownloadStatus } from "@/modules/notifications/utils/types.ts";

import { replaceErrorsInJsonStringification } from "../app-utils/graphql.ts";
import type { IDownloadFileInput } from "../redux/modules/data-export.ts";
import { AUTH_KEY } from "../settings/settings.ts";

type AuthHeaders = Record<string, string | undefined>;
type Resolve = (done: boolean | PromiseLike<boolean>) => void;
type Reject = (reason?: Error) => void;

export default class FileDownloadClient {
  private apiUrl: string;
  private getAuthHeaders: () => AuthHeaders;

  constructor(apiUrl: string, getAuthHeaders: () => AuthHeaders) {
    this.apiUrl = apiUrl;
    this.getAuthHeaders = getAuthHeaders;
  }

  /************** Management of file downloads (promise resolved via the WS channel ******************/

  private pendingFileDownloads: Record<string, { resolve: Resolve; reject: Reject; status?: FileDownloadStatus }> = {}; // map by downloadId/fileKey ; list of pending Promise resolvers/rejecters corresponding to each of the pending file downloads

  private watchFileDownload(fileKey: string, resolve: Resolve, reject: Reject) {
    this.pendingFileDownloads[fileKey] = { resolve, reject };
  }

  public unwatchFileDownload(fileKey: string) {
    delete this.pendingFileDownloads[fileKey];
  }

  public updateFileDownloadStatus(fileKey: string, status: FileDownloadStatus) {
    if (this.pendingFileDownloads[fileKey]) {
      this.pendingFileDownloads[fileKey].status = status;
    }
  }

  public getFileDownloadPromise(fileKey: string) {
    return this.pendingFileDownloads[fileKey];
  }

  // a hidden iframe is needed so that the browser does not consider we are navigating away from the current page,
  // and therefore close the web socket connection
  public static DOWNLOAD_IFRAME_TARGET_NAME = "downloadTarget";

  // if we need the browser to manage the download by itself (so that we do not have to load the file into memory
  // before saving it via javascript, and it is possible to cancel and watch the progress)
  // then we cannot use a XMLHttpRequest, we need to use either a GET request or a POSTed form.
  // We strive for a generic solution so we need to create dynamically a FORM and dynamically submit it together with
  // the payload in the body.
  // The Authorization header cannot be modified with this mechanism therefore we provide it as part of the form data.
  public downloadFile(endpoint: string, inputParams: IDownloadFileInput): string {
    const form = document.createElement("form");
    const payloadInput = document.createElement("input");
    const fileKeyInput = document.createElement("input");
    const authToken = document.createElement("input");

    form.action = this.apiUrl + endpoint;
    form.method = "POST";
    // using a hidden iframe as target of the form request is needed so that the browser does not consider
    // we are navigating away from the current page, and therefore close the web socket connection
    form.target = FileDownloadClient.DOWNLOAD_IFRAME_TARGET_NAME; // using "_blank would open another tab temporarily
    form.style.display = "none";

    payloadInput.setAttribute("name", "payload");
    payloadInput.setAttribute("value", JSON.stringify(inputParams, replaceErrorsInJsonStringification));
    form.appendChild(payloadInput);

    const headers = this.getAuthHeaders();

    authToken.setAttribute("name", AUTH_KEY);
    authToken.setAttribute("value", headers[AUTH_KEY]!);
    form.appendChild(authToken);

    fileKeyInput.setAttribute("name", "fileKey");

    const fileKey = createRandomFileKey();

    fileKeyInput.setAttribute("value", fileKey);
    form.appendChild(fileKeyInput);

    document.body.appendChild(form);
    form.submit();

    // Once the form is sent, remove it.
    document.body.removeChild(form);

    return fileKey;
  }

  public downloadFileAndWatchCompletion(endpoint: string, inputParams: IDownloadFileInput): Promise<boolean> {
    const fileKey = this.downloadFile(endpoint, inputParams);
    // Unfortunately it is not possible to get informed from the browser when the file download starts or ends
    // so we use the Web Sockets channel :
    // put combination of download identifier and promise resolver in a local object, to be resolved() from the Web Sockets stream
    // IN CASE OF ERROR: the error of the form submit cannot be captured and therefore the promise cannot be rejected correctly...
    return new Promise<boolean>((resolve, reject) => this.watchFileDownload(fileKey, resolve, reject));
  }
}
